import { Injectable } from "@angular/core";
import { CustomRule, Filter, DisplayOptions, AdvancedConditions, CosmeticOptions } from "../../models/filter-models";
import { DisplayType, LabelSize } from "../../models/enums";
import { FlatItemData, Tier } from "../../models/item-data-models";
import { ItemDataService } from "../item-data.service";
import { filterHeader } from "../../filter-template";
import { RuleGeneratorService } from "./rule-generator.service";
import { CurrencyTierService } from "../tiering/currency-tier.service";
import { poe1 } from "../../../game";

@Injectable({
  providedIn: 'root'
})
export class CurrencyGeneratorService {
  private flatItemData!: FlatItemData[];
  private tieredItemTypes: string[];
  private itemColors!: { normal: string, magic: string, rare: string, currency: string };
  private hiddenCurrencyColor!: string;
  private hiddenCurrencyBackgroundColor!: string;

  currencyWithStacks: string[];
  currencyTypesToPromoteSmallStacks: string[] = poe1 ? ['Scarabs', 'Delirium Orbs'] : [];

  static cache: { [key: string]: string } = {};

  constructor(private itemDataService: ItemDataService, private ruleGenerator: RuleGeneratorService, private currencyTierService: CurrencyTierService) {
    this.flatItemData = this.itemDataService.getFlatItemData();
    this.tieredItemTypes = this.itemDataService.getTieredCurrencyItemTypes();
    this.currencyWithStacks = this.itemDataService.getStackableCurrency();
    this.itemColors = this.itemDataService.getItemColors();
    this.hiddenCurrencyColor = this.itemColors.currency.replace('rgb(', 'rgba(').replace(')', ',0.6)');
    this.hiddenCurrencyBackgroundColor = 'rgba(0, 0, 0, 0.6)';
  }

  public generateTieredCurrencyRules(filter: Filter, disableCache: boolean = false) {
    const cacheKey =
      JSON.stringify(filter.cosmeticSettings)
      + JSON.stringify(filter.tierCosmetics)
      + JSON.stringify(filter.divCardTierCosmetics)
      + JSON.stringify(filter.divCardCosmeticsSameAsTierCosmetics)
      + (filter.ruthless ? 'ruthless' : '')
      + CurrencyTierService.cacheBreaker
      + (disableCache ? new Date().getTime() : '');

    if (CurrencyGeneratorService.cache[cacheKey])
      return CurrencyGeneratorService.cache[cacheKey];

    const tiers: Tier[] = Object.values(Tier);
    const tierListData = this.currencyTierService.getTierList();

    let text = '';

    for (let itemType of this.tieredItemTypes) {
      if (itemType === 'Uniques') continue;
      if (filter.ruthless && this.itemDataService.getRuthlessDisabledCurrencyTypes().includes(itemType)) continue;

      const tierCosmetics = itemType === 'Divination Cards' ? filter.divCardTierCosmetics : filter.tierCosmetics;
      const displayType = itemType === 'Divination Cards' ? DisplayType.DivCardTier : DisplayType.Tier;

      text += filterHeader.replace('{header}', itemType);
      for (let tier of tiers) {
        const tierData = tierListData.find(t => t.tier === tier);
        if (tierData) {
          const baseTypes = tierData.itemTypes.find(i => i.itemType === itemType)?.baseTypes || [];
          const baseTypePerClass: { [key: string]: string[] } = this.flatItemData.filter(i => baseTypes.some(bt => bt.name === i.baseType.name)).reduce((acc: any, curr) => {
            if (!acc[curr.itemClass]) {
              acc[curr.itemClass] = [];
            }
            acc[curr.itemClass].push(curr.baseType.name);
            return acc;
          }, {});

          let increaseOneTierThreshold = filter.cosmeticSettings?.stackedGeneralCurrencyOneTierThreshold || 3;
          let increaseTwoTiersThreshold = filter.cosmeticSettings?.stackedGeneralCurrencyTwoTiersThreshold || 10;

          const hiddenTier = tierCosmetics.find(t => t.tier === tier)?.displayOptions?.hidden;
          if (hiddenTier && filter.cosmeticSettings?.stackedHiddenCurrencyPromotionEnabled && filter.cosmeticSettings?.stackedHiddenCurrencyOneTierThreshold) {
            increaseOneTierThreshold = filter.cosmeticSettings?.stackedHiddenCurrencyOneTierThreshold;
          }
          if (hiddenTier && filter.cosmeticSettings?.stackedHiddenCurrencyPromotionEnabled && filter.cosmeticSettings?.stackedHiddenCurrencyTwoTiersThreshold) {
            increaseTwoTiersThreshold = filter.cosmeticSettings?.stackedHiddenCurrencyTwoTiersThreshold;
          }

          for (let itemClass in baseTypePerClass) {
            let baseTypesOfClass = baseTypePerClass[itemClass];
            const displayItemClass = tier + '-Tier ' + (itemClass === 'Stackable Currency' ? itemType : itemClass);

            if (filter.cosmeticSettings?.stackedGeneralCurrencyPromotionEnabled) {
              const isTypeToPromoteSmallStacks = this.currencyTypesToPromoteSmallStacks.includes(itemType);
              if (this.currencyWithStacks.some(c => baseTypesOfClass.includes(c)) || isTypeToPromoteSmallStacks) {
                let stackableBaseTypes = isTypeToPromoteSmallStacks ? baseTypesOfClass : baseTypesOfClass.filter((b: string) => this.currencyWithStacks.includes(b));
                if (!filter.cosmeticSettings?.stackedGeneralCurrencyNeverPromote.includes('None') && filter.cosmeticSettings?.stackedGeneralCurrencyNeverPromote.length > 0) {
                  stackableBaseTypes = stackableBaseTypes.filter((b: string) => !filter.cosmeticSettings?.stackedGeneralCurrencyNeverPromote.includes(b));
                }

                if (stackableBaseTypes.length > 0) {
                  if (!isTypeToPromoteSmallStacks) {
                    // Large stacks only for the regular case
                    const ruleLargeStacks = new CustomRule();
                    ruleLargeStacks.itemClass = itemClass;
                    ruleLargeStacks.baseTypes = stackableBaseTypes;
                    ruleLargeStacks.advancedConditions = new AdvancedConditions({ minStackSize: increaseTwoTiersThreshold });

                    if (hiddenTier && filter.cosmeticSettings?.stackedHiddenCurrencyPromotionEnabled && filter.cosmeticSettings?.stackedHiddenCurrencyNeverHighlight) {
                      const twoTiersHigher = this.getHigherTier(this.getHigherTier(tier));
                      const twoTiersHigherCosmeticCopy = new CosmeticOptions(tierCosmetics.find(t => t.tier === twoTiersHigher)?.displayOptions?.cosmeticOptions);
                      twoTiersHigherCosmeticCopy.highlight = false;
                      ruleLargeStacks.displayOptions = new DisplayOptions({ displayType: DisplayType.Custom, cosmeticOptions: twoTiersHigherCosmeticCopy });
                    } else {
                      ruleLargeStacks.displayOptions = new DisplayOptions({ displayType: displayType, displayTypeTier: this.getHigherTier(this.getHigherTier(tier)) });
                    }

                    text += '\n' + this.ruleGenerator.generateRule(ruleLargeStacks, filter, 'currency-large-stacks', 'Large Stacks of ' + displayItemClass) + '\n';
                  }

                  // Small stacks also (and always) for currencyTypesToPromoteSmallStacks tier <= orange
                  if (!isTypeToPromoteSmallStacks || ([Tier.BOrange, Tier.CYellow, Tier.DWhite, Tier.EVisible, Tier.FHidden].includes(tier) && isTypeToPromoteSmallStacks)) {
                    const ruleSmallStacks = new CustomRule();
                    ruleSmallStacks.itemClass = itemClass;
                    ruleSmallStacks.baseTypes = stackableBaseTypes;
                    ruleSmallStacks.advancedConditions = new AdvancedConditions({ minStackSize: increaseOneTierThreshold });

                    if (hiddenTier && filter.cosmeticSettings?.stackedHiddenCurrencyPromotionEnabled && filter.cosmeticSettings?.stackedHiddenCurrencyNeverHighlight) {
                      const oneTierHigher = this.getHigherTier(tier);
                      const oneTierHigherCosmeticCopy = new CosmeticOptions(tierCosmetics.find(t => t.tier === oneTierHigher)?.displayOptions?.cosmeticOptions);
                      oneTierHigherCosmeticCopy.highlight = false;
                      ruleSmallStacks.displayOptions = new DisplayOptions({ displayType: DisplayType.Custom, cosmeticOptions: oneTierHigherCosmeticCopy });
                    } else {
                      ruleSmallStacks.displayOptions = new DisplayOptions({ displayType: displayType, displayTypeTier: this.getHigherTier(tier) });
                    }

                    text += '\n' + this.ruleGenerator.generateRule(ruleSmallStacks, filter, 'currency-small-stacks', 'Small Stacks of ' + displayItemClass) + '\n';
                  }
                }
              }
            }

            const rule = new CustomRule();
            rule.itemClass = itemClass;
            rule.baseTypes = baseTypesOfClass;
            if (itemClass === 'Vault Keys' && hiddenTier) {
              rule.displayOptions = new DisplayOptions({
                displayType: DisplayType.Custom,
                hidden: false,
                cosmeticOptions: new CosmeticOptions({
                  fontSize: LabelSize.Smallest,
                  backgroundColor: this.hiddenCurrencyBackgroundColor,
                  textColor: this.hiddenCurrencyColor,
                  borderColor: this.hiddenCurrencyColor,
                })
              });
            } else {
              rule.displayOptions = new DisplayOptions({ displayType: displayType, displayTypeTier: tier });
            }

            text += '\n' + this.ruleGenerator.generateRule(rule, filter, 'currency', displayItemClass) + '\n';
          }
        }
      }
    }

    CurrencyGeneratorService.cache[cacheKey] = text;

    return text;
  }

  getHigherTier(tier: Tier) {
    const tiers: Tier[] = Object.values(Tier);
    const index = tiers.indexOf(tier);
    return tiers[Math.max(0, index - 1)];
  }
}
